import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ClientLayout from "@/components/ClientLayout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { goToOffers } from "@/lib/offers";
import { deriveNextStep } from "@/lib/nextStep";
import { ProvenanceBadge } from "@/components/Badge";
import SetupHintCard from "@/components/SetupHintCard";
import { CoachingModeCard } from "@/components/CoachingModeCard";
import { CycleCompleteCard } from "@/components/CycleCompleteCard";
import HintBubble from "@/components/HintBubble";
import { ProgramGenerating, ProgramGenState } from "@/components/ProgramGenerating";
import { ArrowRight, Sparkles, Clock, Loader2, CalendarDays, Flag } from "lucide-react";
import { toast } from "sonner";

export default function ClientHome() {
  const { user, checkAuth } = useAuth();
  const [data, setData] = useState(null);
  const [today, setToday] = useState(undefined);
  const [gen, setGen] = useState(false);
  const [genStatus, setGenStatus] = useState(undefined); // processing|success|v1_fallback|error|security_block|needs_bilan|none
  const [preview, setPreview] = useState(null); // aperçu déterministe montré pendant la génération
  const pollRef = useRef();
  const genAttemptsRef = useRef(0);   // borne anti-boucle sur l'état "none" pré-génération
  const genTriggeredRef = useRef(false); // apply-bilan déjà déclenché par le poll ?
  const genStartRef = useRef(Date.now()); // plafond de temps d'attente absolu
  const [renewing, setRenewing] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoChecking, setPromoChecking] = useState(false);
  const [race, setRace] = useState(null);
  const [raceForm, setRaceForm] = useState({ race_name: "", race_date: "", distance: "" });
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [ctaLoading, setCtaLoading] = useState(false);
  const [startFrom, setStartFrom] = useState(null); // prix « à partir de » dynamique (min des offres)
  const [startFromDisplay, setStartFromDisplay] = useState(null);
  const [plansMap, setPlansMap] = useState({}); // key -> {price_period_display, price_display}
  const [rdvDisplay, setRdvDisplay] = useState(null);
  const [weeksPerCycle, setWeeksPerCycle] = useState(null);
  const [welcome, setWelcome] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/plans").then((r) => {
      const prices = (r.data.plans || []).map((p) => p.promo_price || p.price).filter((x) => typeof x === "number");
      if (prices.length) setStartFrom(Math.min(...prices));
      const cheapest = (r.data.plans || []).filter((p) => (p.promo_price || p.price) > 0)
        .sort((a, b) => (a.promo_price || a.price) - (b.promo_price || b.price))[0];
      if (cheapest) setStartFromDisplay(cheapest.price_period_display);
      const m = {};
      (r.data.plans || []).forEach((p) => { m[p.key] = p; });
      setPlansMap(m);
      if (r.data.rdv_unique) setRdvDisplay(r.data.rdv_unique.price_display);
      if (r.data.weeks_per_cycle) setWeeksPerCycle(r.data.weeks_per_cycle);
    }).catch(() => {});
    api.get("/welcome/status").then((r) => setWelcome(r.data)).catch(() => {});
  }, []);

  const buyRdv = async () => {
    setCtaLoading(true);
    try {
      const c = await api.post("/payments/checkout", { plan: "rdv_unique", origin_url: window.location.origin });
      window.location.href = c.data.checkout_url;
    } catch { toast.error("Paiement indisponible pour le moment"); setCtaLoading(false); }
  };

  const loadAnalysis = async () => {
    // Déclenchement AUTO paresseux (dimanche soir / semaine terminée) — silencieux, économe en tokens.
    try {
      const a = await api.post("/coaching/auto-analyze-week");
      if (a.data?.auto_ran && a.data?.analyzed) {
        setAnalysis(a.data);
        if (a.data.message) toast.success(a.data.message);
        await load();
        return;
      }
    } catch {}
    try { const r = await api.get("/coaching/last-analysis"); if (r.data.has_analysis) setAnalysis(r.data); } catch {}
  };

  const analyzeWeek = async () => {
    setAnalyzing(true);
    try {
      const r = await api.post("/coaching/analyze-week");
      if (!r.data.analyzed) { toast.info(r.data.message); return; }
      setAnalysis(r.data);
      toast.success(r.data.message);
      await load();
    } catch { toast.error("Analyse indisponible"); }
    finally { setAnalyzing(false); }
  };

  const load = async () => {
    const [p, prog, t, cs, rc] = await Promise.all([
      api.get("/profile"),
      api.get("/program"),
      api.get("/sessions/today"),
      api.get("/program/cycle-status").catch(() => ({ data: {} })),
      api.get("/profile/race").catch(() => ({ data: {} })),
    ]);
    setData({ profile: p.data.profile, bilan: p.data.bilan, program: prog.data, cycle: cs.data });
    setToday(t.data);
    setRace(rc.data && rc.data.race_date ? rc.data : null);
  };
  useEffect(() => { load(); loadAnalysis(); }, []);

  // Aperçu CONCRET (déterministe) montré pendant la génération : depuis les réponses locales
  // (avant migration) sinon depuis le profil enregistré. Aucun appel LLM.
  const fetchPreview = useCallback(async () => {
    try {
      const raw = localStorage.getItem("mai_bilan_answers");
      if (raw) {
        const r = await api.post("/public/bilan", JSON.parse(raw));
        if (r.data?.synthesis) { setPreview(r.data.synthesis); return; }
      }
      const r = await api.get("/onboarding/preview");
      if (r.data?.available) setPreview(r.data.synthesis);
    } catch {}
  }, []);

  // Suivi HONNÊTE de la génération du 1er programme avec CONDITIONS TERMINALES strictes
  // (plus de boucle infinie) : on borne les tentatives, on déclenche nous-mêmes la migration
  // du bilan si besoin (idempotent serveur), et on impose un plafond de temps absolu.
  const checkGen = useCallback(async () => {
    try {
      const r = await api.get("/onboarding/generation-status");
      let s = r.data.status;
      const raw = localStorage.getItem("mai_bilan_answers");
      const pending = !!raw;

      // Plafond absolu : une attente > 10 min = anomalie -> état terminal réessayable.
      if ((s === "processing" || (s === "none" && pending)) &&
          Date.now() - genStartRef.current > 10 * 60 * 1000) {
        setGenStatus("error");
        return;
      }

      if (s === "none" && pending) {
        // Le job n'est pas encore parti (migration AuthContext pas passée / échouée).
        // On DÉCLENCHE nous-mêmes apply-bilan (idempotent côté serveur) au lieu de tourner sans fin.
        if (!genTriggeredRef.current) {
          genTriggeredRef.current = true;
          try {
            const ap = await api.post("/onboarding/apply-bilan", JSON.parse(raw));
            localStorage.removeItem("mai_bilan_answers");
            s = ap.data?.generation_status || "processing";
          } catch { genTriggeredRef.current = false; }
        }
        genAttemptsRef.current += 1;
        if (s === "none") {
          // Toujours rien après plusieurs relances -> terminal réessayable (jamais de spinner éternel).
          if (genAttemptsRef.current > 12) { setGenStatus("error"); return; }
          setGenStatus("processing");
          pollRef.current = setTimeout(checkGen, 2000);
          return;
        }
      }

      setGenStatus(s);
      if (s === "processing") {
        pollRef.current = setTimeout(checkGen, 3000);
      } else if (s === "success" || s === "v1_fallback") {
        await load();
      }
      // error | security_block | needs_bilan | none(sans bilan en attente) => TERMINAL : on arrête le poll.
    } catch {
      pollRef.current = setTimeout(checkGen, 5000);
    }
  }, []);
  useEffect(() => { checkGen(); fetchPreview(); return () => clearTimeout(pollRef.current); }, [checkGen, fetchPreview]);

  const retryGen = async () => {
    clearTimeout(pollRef.current);
    genAttemptsRef.current = 0;
    genTriggeredRef.current = false;
    genStartRef.current = Date.now();
    setGenStatus("processing");
    try { await api.post("/onboarding/retry-generation"); } catch {}
    checkGen();
  };

  const saveRace = async () => {
    if (!raceForm.race_name || !raceForm.race_date) return toast.error("Nom et date requis");
    await api.post("/profile/race", raceForm);
    setRaceForm({ race_name: "", race_date: "", distance: "" });
    await load();
    toast.success("Course enregistrée — l'affûtage sera calé automatiquement");
  };

  const [renewMsg, setRenewMsg] = useState(null);
  const renew = async () => {
    if (renewing) return;
    setRenewing(true); setRenewMsg(null);
    // renewal=1 UNIQUEMENT si le cycle courant est réellement terminé ; sinon l'achat déverrouille la suite du cycle.
    const cycleDone = !!data?.cycle?.finished && !(today && today.locked);
    try {
      const r = await api.post("/program/renew");
      if (r.data.regenerated) {
        await load();
        toast.success("Nouveau cycle prêt — plus avancé que le précédent");
        setRenewing(false);
        return;
      }
      // Paiement requis -> page de vente (comparaison). Aucun checkout direct, aucune offre présélectionnée.
      goToOffers(navigate, { source: "fin_cycle", returnTo: "/app", renewal: cycleDone });
    } catch (e) {
      const st = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (st === 403) { goToOffers(navigate, { source: "acces_expire", returnTo: "/app", renewal: false }); return; }
      if (st === 409) setRenewMsg({ kind: "blocked", text: detail || "Génération bloquée par sécurité : aucun programme sûr n'a pu être produit. Ton coach est alerté." });
      else if (st === 503) setRenewMsg({ kind: "retry", text: detail || "Génération momentanément indisponible. Réessaie dans un instant — ton programme et tes séances restent accessibles." });
      else setRenewMsg({ kind: "retry", text: detail || "Le cycle suivant n'a pas pu être préparé. Ton programme actuel est conservé, réessaie." });
      setRenewing(false);
    }
  };
  const renewNotice = renewMsg ? (
    <p data-testid={renewMsg.kind === "blocked" ? "renew-blocked" : "renew-retry"}
      className={`mt-4 max-w-md rounded-xl border px-4 py-3 text-sm ${renewMsg.kind === "blocked" ? "border-destructive/40 bg-destructive/5 text-destructive" : "border-amber-500/40 bg-amber-500/5 text-obsidian/80"}`}>
      {renewMsg.text}
    </p>
  ) : null;

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoChecking(true);
    try {
      const r = await api.post("/promo/validate", { code, plan: user?.plan || "essentiel" });
      setPromoApplied(r.data);
      toast.success(`Code ${r.data.code} appliqué`);
    } catch (e) {
      setPromoApplied(null);
      toast.error(e?.response?.data?.detail || "Code promo invalide");
    } finally { setPromoChecking(false); }
  };

  const promoBlock = (
    <div className="mt-5 max-w-sm" data-testid="renew-promo">
      <label className="text-xs tracking-[0.14em] uppercase font-semibold text-obsidian/50">Code promo</label>
      <div className="mt-2 flex gap-2">
        <input
          data-testid="renew-promo-input"
          value={promoInput}
          onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoApplied(null); }}
          placeholder="Ton code"
          className="flex-1 rounded-full border border-border bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-sage uppercase"
        />
        <button data-testid="renew-promo-apply" onClick={applyPromo} disabled={promoChecking || !promoInput.trim()}
          className="rounded-full border border-obsidian px-4 py-2.5 text-sm font-medium hover:bg-obsidian hover:text-white transition-colors disabled:opacity-50">
          {promoChecking ? "…" : "Appliquer"}
        </button>
      </div>
      {promoApplied && (
        <p data-testid="renew-promo-applied" className="mt-2 text-sm text-sage">
          Code {promoApplied.code} appliqué · {promoApplied.base} € → {promoApplied.discounted} €
        </p>
      )}
    </div>
  );

  const generate = async () => {
    setGen(true);
    try {
      await api.post("/program/generate");
      await load();
      toast.success("Ta programmation est prête");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setGen(false); }
  };

  if (!data) return <ClientLayout><div className="pulse-organic w-3 h-3 rounded-full bg-sage mx-auto mt-20" /></ClientLayout>;

  const hasProfile = data.profile?.status === "complete";
  const active = user?.accompaniment_active;
  // Prochaine étape (orientation post-paiement) — mêmes règles que l'écran de succès.
  const nextStep = deriveNextStep({ profile: data.profile, program: data.program, cycle: data.cycle });

  // Écran de génération : on l'affiche tant que MAI construit réellement le 1er programme
  // (statut processing), et pendant la fenêtre initiale où le job n'est pas encore connu.
  const pendingBilan = typeof window !== "undefined" && !!localStorage.getItem("mai_bilan_answers");
  const showGenerating = genStatus === "processing"
    || (genStatus === undefined && (pendingBilan || (hasProfile && !data.program)));
  if (showGenerating) return <ClientLayout><ProgramGenerating preview={preview} /></ClientLayout>;
  if (!data.program && genStatus === "error")
    return <ClientLayout><ProgramGenState variant="error" onRetry={retryGen} /></ClientLayout>;
  if (!data.program && genStatus === "security_block")
    return <ClientLayout><ProgramGenState variant="blocked" onRetry={() => navigate("/bilan")} /></ClientLayout>;
  if (!data.program && genStatus === "needs_bilan")
    return <ClientLayout><ProgramGenState variant="needs_bilan" onRetry={() => navigate("/bilan")} /></ClientLayout>;

  return (
    <ClientLayout>
      {welcome?.active && (
        <div data-testid="welcome-offer-banner"
          className={`fade-up mb-6 rounded-2xl border p-4 ${welcome.notify ? "border-amber-500/40 bg-amber-500/[0.07]" : "border-sage/40 bg-sage/[0.06]"}`}>
          <div className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase font-semibold text-sage">
            <Sparkles size={14} /> Accès offert{welcome.mode === "campaign" ? " · campagne" : ""}
          </div>
          {welcome.notify ? (
            <p className="mt-2 text-sm text-obsidian/75" data-testid="welcome-offer-end-notice">
              Ton accès offert se termine dans {welcome.days_left} jour{welcome.days_left > 1 ? "s" : ""}
              {welcome.continue_offer
                ? <>. Poursuis avec MAI pour {welcome.continue_offer.price_period_display}.</>
                : <>. Consulte les offres disponibles pour poursuivre.</>}
            </p>
          ) : (
            <p className="mt-2 text-sm text-obsidian/75">
              Profite de toutes les fonctionnalités gratuitement{typeof welcome.days_left === "number" ? ` pendant encore ${welcome.days_left} jour${welcome.days_left > 1 ? "s" : ""}` : ""}.
            </p>
          )}
          {welcome.notify && (
            <button data-testid="welcome-offer-continue" onClick={() => navigate("/tarifs")}
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-obsidian text-white px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform">
              Voir les offres <ArrowRight size={15} />
            </button>
          )}
        </div>
      )}
      {active && nextStep?.dashboard && (
        <div data-testid="next-step-banner"
          className="fade-up mb-6 rounded-2xl border border-sage/40 bg-sage/[0.06] p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <span className="shrink-0 text-xs tracking-[0.14em] uppercase font-semibold text-sage">Prochaine étape</span>
            <span className="text-sm text-obsidian/75">{nextStep.dashboard}</span>
          </div>
          {nextStep.route !== "/app" && (
            <button data-testid="next-step-cta" onClick={() => navigate(nextStep.route)}
              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-obsidian text-white px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform">
              {nextStep.label} <ArrowRight size={15} />
            </button>
          )}
        </div>
      )}
      {!hasProfile && (
        <div className="fade-up">
          <h1 className="text-3xl font-light tracking-tight">Bienvenue{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.</h1>
          <p className="mt-3 text-obsidian/60">Commence par ton bilan sportif avec l'assistant IA. Gratuit, sans engagement.</p>
          <button data-testid="home-start-bilan" onClick={() => navigate("/bilan")}
            className="mt-8 inline-flex items-center gap-3 rounded-full bg-obsidian text-white px-6 py-4 font-medium hover:-translate-y-0.5 transition-transform">
            <Sparkles size={17} /> Faire mon bilan IA <ArrowRight size={16} />
          </button>
        </div>
      )}

      {hasProfile && !active && (
        <div className="fade-up">
          <ProvenanceBadge type="analyse">TON BILAN EST PRÊT</ProvenanceBadge>
          <h1 className="mt-6 text-3xl font-light tracking-tight">{data.bilan?.synthesis?.resume ? "Ton profil est analysé." : "Ton programme t'attend."}</h1>
          <p className="mt-4 text-obsidian/70 leading-relaxed">{data.bilan?.synthesis?.resume || "MAI a construit ta préparation. Ta première séance est offerte — découvre-la, puis débloque la suite quand tu veux."}</p>

          {data.program && today && (
            <div className="mt-8 rounded-2xl border border-sage/40 bg-sage/[0.05] p-6" data-testid="free-session-card">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs tracking-[0.14em] uppercase font-semibold text-sage">Ta 1re séance · offerte</span>
              </div>
              <h3 className="mt-2 text-2xl font-light tracking-tight">{today.title}</h3>
              <div className="mt-2 flex items-center gap-2 text-sm text-obsidian/60">
                <Clock size={15} strokeWidth={1.5} /> {today.duration_min} min · {today.exercises?.length || 0} exercices
              </div>
              {!today.locked ? (
                <button data-testid="free-session-start" onClick={() => navigate(`/seance/${today.session_id}`)}
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-obsidian text-white px-6 py-3 text-sm font-medium hover:-translate-y-0.5 transition-transform">
                  Faire ma séance offerte <ArrowRight size={16} />
                </button>
              ) : (
                <p className="mt-3 text-sm text-obsidian/50">Séance verrouillée.</p>
              )}
            </div>
          )}

          {data.program && (
            <button data-testid="home-see-program" onClick={() => navigate("/programme")}
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-obsidian/20 px-6 py-3 text-sm font-medium hover:bg-obsidian/[0.03] transition-colors">
              Voir tout mon programme <ArrowRight size={15} />
            </button>
          )}

          <div className="mt-8 rounded-xl bg-obsidian text-white p-7">
            <h3 className="text-lg font-medium">Débloque toute ta préparation</h3>
            <p className="mt-2 text-white/60 text-sm">Programmation IA complète + chat illimité + adaptations{startFromDisplay ? ` dès ${startFromDisplay}` : ""}. Accompagnement humain avec un coach en Premium.</p>
            <button data-testid="home-goto-pricing" onClick={() => navigate("/tarifs")}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-white text-obsidian px-6 py-3 font-medium hover:-translate-y-0.5 transition-transform">
              Voir les offres <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {hasProfile && active && !data.program && (
        <div className="fade-up text-center pt-10">
          <ProvenanceBadge type="analyse">ACCOMPAGNEMENT ACTIVÉ</ProvenanceBadge>
          <h1 className="mt-6 text-3xl font-light tracking-tight">Génère ta programmation</h1>
          <p className="mt-3 text-obsidian/60 max-w-md mx-auto">Le moteur va analyser ton profil, appliquer les règles et sélectionner tes exercices.</p>
          <button data-testid="home-generate-program" onClick={generate} disabled={gen}
            className="mt-8 inline-flex items-center gap-3 rounded-full bg-obsidian text-white px-7 py-4 font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-60">
            {gen ? <><Loader2 size={17} className="animate-spin" /> Assemblage…</> : <><Sparkles size={17} /> Générer mon programme</>}
          </button>
        </div>
      )}

      {hasProfile && active && data.program && (
        <div className="fade-up">
          <SetupHintCard />
          <CoachingModeCard onChange={load} />
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-obsidian/40">
            <span className="relative flex h-1.5 w-1.5">
              <span className="pulse-organic absolute inline-flex h-full w-full rounded-full bg-sage opacity-70" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-sage" />
            </span>
            Aujourd'hui
          </div>
          {today && !today.locked ? (
            <div className="mt-4">
              {today.week_label && (
                <div className="flex items-center gap-2" data-testid="today-week-label">
                  <span className="text-xs tracking-[0.12em] uppercase font-semibold text-sage">{today.week_label}</span>
                  {today.taper
                    ? <span className="text-[10px] tracking-[0.12em] uppercase font-semibold rounded-full bg-[#FBF0DC] text-[#8C6B16] border border-[#EAD8B0] px-2 py-0.5">Affûtage</span>
                    : today.deload
                      ? <span className="text-[10px] tracking-[0.12em] uppercase font-semibold rounded-full bg-[#F0F4F1] text-[#4A6B53] border border-[#D5E3D8] px-2 py-0.5">Allègement</span>
                      : null}
                </div>
              )}
              <h1 className="mt-2 text-4xl sm:text-5xl font-light tracking-tight leading-tight">{today.title}</h1>
              <div className="mt-4 flex items-center gap-2 text-obsidian/60">
                <Clock size={16} strokeWidth={1.5} /> {today.duration_min} minutes · {today.exercises?.length || 0} exercices
              </div>
              {today.adapted && <ProvenanceBadge type="adaptation" className="mt-4">SÉANCE ADAPTÉE</ProvenanceBadge>}
              <HintBubble
                hintKey="first_session"
                enabled={!!user?.tutorial_completed}
                className="mt-6 max-w-lg"
                title="Voici ta séance du jour"
                body="Prends connaissance du contenu et de la durée, puis lance-toi avec le bouton COMMENCER. Chaque exercice détaille l'allure et le ressenti attendus."
              />
              <button data-testid="start-session-button" onClick={() => navigate(`/seance/${today.session_id}`)}
                className="mt-10 w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-full bg-obsidian text-white px-10 py-5 text-lg font-medium hover:-translate-y-0.5 transition-transform">
                COMMENCER <ArrowRight size={20} />
              </button>
              <p className="mt-6 text-sm text-obsidian/50 max-w-lg leading-relaxed">{today.reason}</p>
            </div>
          ) : today && today.locked ? (
            <div className="mt-4" data-testid="today-locked">
              <ProvenanceBadge type="analyse">CYCLE SUIVANT</ProvenanceBadge>
              <h1 className="mt-4 text-3xl font-light tracking-tight">Prochain cycle verrouillé</h1>
              <p className="mt-3 text-obsidian/60 max-w-md">Tu as terminé les séances accessibles de ton cycle. Débloque la suite pour continuer ta progression.</p>
              {promoBlock}
              <button data-testid="today-unlock-button" onClick={renew} disabled={renewing}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-obsidian text-white px-7 py-4 font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-60 disabled:hover:translate-y-0">
                {renewing ? "Redirection…" : "Débloquer la suite"} <ArrowRight size={16} />
              </button>
              {renewNotice}
              <CycleCompleteCard />
            </div>
          ) : data.cycle?.finished ? (
            <div className="mt-4" data-testid="cycle-renewal">
              <ProvenanceBadge type="analyse">CYCLE TERMINÉ</ProvenanceBadge>
              <h1 className="mt-4 text-3xl font-light tracking-tight">Bravo, cycle bouclé 🎉</h1>
              {data.cycle?.prepaid_cycles_remaining > 0 ? (
                <>
                  <p className="mt-3 text-obsidian/60 max-w-md">Tu as terminé ce cycle. Il te reste {data.cycle.prepaid_cycles_remaining} cycle{data.cycle.prepaid_cycles_remaining > 1 ? "s" : ""} prépayé{data.cycle.prepaid_cycles_remaining > 1 ? "s" : ""} dans ton programme complet.</p>
                  <button data-testid="renew-button" onClick={renew} disabled={renewing}
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-obsidian text-white px-7 py-4 font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-60 disabled:hover:translate-y-0">
                    {renewing ? "Préparation…" : "Lancer le cycle suivant (inclus)"} <ArrowRight size={16} />
                  </button>
                  {renewNotice}
                </>
              ) : (
                <>
                  <p className="mt-3 text-obsidian/60 max-w-md">Tu as terminé ton cycle{weeksPerCycle ? ` de ${weeksPerCycle} semaines` : ""}. Renouvelle pour lancer le prochain cycle et continuer ta progression.</p>
                  {promoBlock}
                  <button data-testid="renew-button" onClick={renew} disabled={renewing}
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-obsidian text-white px-7 py-4 font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-60 disabled:hover:translate-y-0">
                    {renewing ? "Redirection…" : "Choisir mon offre pour continuer"} <ArrowRight size={16} />
                  </button>
                  {renewNotice}
                </>
              )}
              <CycleCompleteCard />
            </div>
          ) : (
            <div className="mt-4">
              <h1 className="text-3xl font-light tracking-tight">Repos aujourd'hui</h1>
              <p className="mt-3 text-obsidian/60">Récupération : la progression se construit aussi au repos. Consulte ton plan.</p>
              <button onClick={() => navigate("/programme")} className="mt-6 inline-flex items-center gap-2 rounded-full border border-obsidian px-5 py-3 text-sm font-medium hover:bg-obsidian hover:text-white transition-[background-color,color]">Voir mon plan <ArrowRight size={15} /></button>
            </div>
          )}

          {!data.cycle?.finished && !(today && today.locked) && (
          <div className="mt-8 rounded-2xl border border-border bg-white p-5" data-testid="weekly-analysis-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs tracking-[0.14em] uppercase font-semibold text-sage">Suivi de ta progression</div>
                <p className="mt-1 text-sm text-obsidian/60 max-w-md">Tes derniers retours sont pris en compte pour suivre l'évolution de ton entraînement et détecter lorsqu'un ajustement peut être nécessaire.</p>
              </div>
              <button data-testid="analyze-week-button" onClick={analyzeWeek} disabled={analyzing}
                className="shrink-0 rounded-full bg-obsidian text-white px-5 py-3 text-sm font-medium inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-50">
                {analyzing ? "Analyse…" : "Analyser ma progression"} <ArrowRight size={15} />
              </button>
            </div>
            {analysis && (
              <div data-testid="weekly-analysis-result" className={`mt-4 rounded-xl border p-4 ${analysis.decision === "ALERTE_COACH" ? "border-destructive/30 bg-destructive/5" : analysis.decision === "RÉDUIRE" ? "border-amber-500/30 bg-amber-500/10" : "border-sage/30 bg-sage/10"}`}>
                <div className="text-sm font-semibold text-obsidian">{analysis.decision === "PROGRESSER" ? "Progression" : analysis.decision === "MAINTENIR" ? "Charge maintenue" : analysis.decision === "RÉDUIRE" ? "Allègement" : "Attention coach"}</div>
                <p className="mt-1 text-sm text-obsidian/75">{analysis.message}</p>
                {analysis.adapted_sessions > 0 && <p className="mt-1.5 text-xs text-obsidian/50">{analysis.adapted_sessions} séance(s) de la semaine suivante ajustée(s).</p>}
                {(analysis.suggest_rdv || analysis.suggest_premium) && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="analysis-alert-ctas">
                    {analysis.suggest_rdv && (
                      <button data-testid="analysis-buy-rdv" onClick={buyRdv} disabled={ctaLoading}
                        className="rounded-full bg-obsidian text-white px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-50">
                        Prendre un RDV à la carte{rdvDisplay ? ` · ${rdvDisplay}` : ""}
                      </button>
                    )}
                    {analysis.suggest_premium && (
                      <button data-testid="analysis-goto-premium" onClick={() => navigate("/tarifs")}
                        className="rounded-full border border-obsidian px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:bg-obsidian hover:text-white transition-[background-color,color]">
                        Passer en Premium
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          )}

        </div>
      )}
    </ClientLayout>
  );
}
